// AnalyticsQuery.jsx
import { useEffect, useState, useRef } from 'react'
import Table from 'AnalyticsDashboard/components/Table'
import TimeSeriesTable from './TimeSeriesTable'
import PivotTable from './PivotTable'
import LineChart from 'AnalyticsDashboard/components/LineChart'
import LineChartV2 from 'AnalyticsDashboard/components/LineChartV2'
import PieChart from 'AnalyticsDashboard/components/PieChart'
import BarChart from 'AnalyticsDashboard/components/BarChart'
import ColumnChartV2 from 'AnalyticsDashboard/components/ColumnChartV2'
import PieChartV2 from 'AnalyticsDashboard/components/PieChartV2'
import GaugeChart from 'AnalyticsDashboard/components/GaugeChart'
import MapChart from 'AnalyticsDashboard/components/MapChart'
import NumberChart from 'AnalyticsDashboard/components/NumberChart'
import ConfidenceIntervalLineChart from 'AnalyticsDashboard/components/ConfidenceIntervalLineChart'
import prepareChartData from 'Analytics/utils/prepareChartData'
import Loading from 'Shared/components/Loading'
import { useAnalyticsDashboard, useAnalyticsQuery, useAnalyticsContext } from 'AnalyticsDashboard/AnalyticsContext'

// Chart type display labels
const CHART_TYPE_LABELS = {
  table: 'Table',
  line: 'Line Chart',
  lineV2: 'Line Chart',
  pie: 'Pie Chart',
  bar: 'Bar Chart',
  columnV2: 'Column Chart',
  barV2: 'Bar Chart',
  pieV2: 'Pie Chart',
  map: 'Map (UK)',
  mapWorld: 'Map (World)',
  timeSeriesTable: 'Time Series Table',
  number: 'Number',
  gauge: 'Gauge',
  confidenceIntervalLineChart: 'Confidence Interval',
  pivotTable: 'Pivot Table',
  json: 'JSON'
}

export default function AnalyticsQuery({
  kind,
  chartType = 'line', // Default chart type
  supportedChartTypes = [], // Array of supported chart types for toggling
  chartOptions,
  controlOptions = { showChartType: false, showTimeBucket: true }, // Whether to show view options
  metrics = [{ id: 'count' }], // Default metrics to show
  dimensions = [],
  primaryDimension = { id: 'time', label: 'Time' },
  secondaryDimension,
  parameters = {},
  global = false,
  orderBy = [],
  limit = null, // Optional limit for the number of results
  overrideFilters = {},
  comparePeriods = null,
  chartComponent = null, // Optional custom chart component
  viewMode = 'chart' // 'chart' | 'table' | 'sql' — controlled externally from header
}) {
  const { registerQuery, unregisterQuery } = useAnalyticsDashboard()

  const idRef = useRef(null)
  if (!idRef.current) idRef.current = _.uniqueId('aq_')
  const id = idRef.current

  // Sync all props to context after each render (deferred to avoid
  // "Cannot update a component while rendering" warning)
  useEffect(() => {
    registerQuery({
      id,
      kind,
      chartType,
      supportedChartTypes,
      chartOptions,
      controlOptions,
      dimensions,
      primaryDimension,
      secondaryDimension,
      metrics,
      parameters,
      global,
      orderBy,
      limit,
      overrideFilters,
      comparePeriods,
      chartComponent
    })

    return () => unregisterQuery(id)
  }, [])

  return <AnalyticsQueryInner id={id} viewMode={viewMode} />
}

function AnalyticsQueryInner({ id, viewMode }) {
  const { query, updateQuery, updateDimension } = useAnalyticsQuery(id)
  const { state: { uiEnabled } } = useAnalyticsContext()
  const { fetchData } = useAnalyticsDashboard()

  // Handle fetching data if query changes
  const queryExists = !!query
  const timeBucket = query?.dimensions?.find(d => d.id === 'time')?.timeBucket || null
  useEffect(() => {
    if (!queryExists) return

    fetchData(id)
  }, [queryExists, timeBucket])

  // Tab state - initialized after we have chartOptions
  const [activeTabId, setActiveTabId] = useState(null)

  if (!query || query.dataLoading) {
    return <Loading />
  }

  const { data: returnedData, dataLoading, chartType, supportedChartTypes = [], controlOptions, metrics: queryMetrics, chartOptions = {}, dimensions, chartComponent } = query
  const { showChartType, showTimeBucket } = controlOptions || {}
  const showChartTypeToggle = supportedChartTypes.length > 1
  const { data, dimensionInfo, sql } = returnedData || {}

  // Handle tabs
  const tabs = chartOptions.tabs || []
  const currentTabId = activeTabId || (tabs.length > 0 ? tabs[0].id : null)
  const activeTab = tabs.find(t => t.id === currentTabId)

  // Merge active tab overrides into chartOptions
  const effectiveChartOptions = activeTab
    ? {
        ...chartOptions,
        y: activeTab.y !== undefined ? activeTab.y : chartOptions.y,
        seriesBy: activeTab.seriesBy !== undefined ? activeTab.seriesBy : chartOptions.seriesBy
      }
    : chartOptions

  const primaryDimension = { ...query.primaryDimension, id: _.camelCase(query.primaryDimension.id) }
  const secondaryDimension = query.secondaryDimension ? { ...query.secondaryDimension, id: _.camelCase(query.secondaryDimension.id) } : null
  const metrics = queryMetrics.map(metric => ({ ...metric, id: _.camelCase(metric.id) }))
  const chartData = data ? prepareChartData({ chartType, data, metrics, primaryDimension, secondaryDimension }) : null

  const showData = data && data.length > 0 && !dataLoading
  const setChartType = (chartType) => updateQuery({ chartType })

  const chartProps = { data: chartData, chartOptions: effectiveChartOptions, dimensionInfo }
  const rawChartProps = { data, chartOptions: effectiveChartOptions, dimensionInfo, query }

  // Determine effective chart type — viewMode 'table' forces table rendering
  const effectiveChartType = viewMode === 'table' ? 'table' : chartType

  const renderChart = () => (
    <>
      {showChartTypeToggle && viewMode === 'chart' && (
        <div className='chart-type-toggle'>
          <select value={chartType} onChange={(e) => setChartType(e.target.value)}>
            {supportedChartTypes.map(type => (
              <option key={type} value={type}>{CHART_TYPE_LABELS[type] || type}</option>
            ))}
          </select>
        </div>
      )}
      {uiEnabled && showChartType && !showChartTypeToggle && viewMode === 'chart' && (
        <div>
          <select value={chartType} onChange={(e) => setChartType(e.target.value)}>
            <option value="table">Table</option>
            <option value="line">Line Chart</option>
            <option value="pie">Pie Chart</option>
            <option value="bar">Bar Chart</option>
            <option value="map">Map UK</option>
            <option value="mapWorld">Map World</option>
          </select>
        </div>
      )}
      {uiEnabled && showTimeBucket && timeBucket && (
        <div className="time-bucket-toggle">
          {['hour', 'day', 'week', 'month', 'quarter', 'year'].map(b => (
            <button
              key={b}
              type="button"
              className={`time-bucket-toggle__btn ${timeBucket === b ? 'active' : ''}`}
              onClick={() => updateDimension('time', { timeBucket: b })}
            >
              {_.capitalize(b)}
            </button>
          ))}
        </div>
      )}
      {tabs.length > 1 && (
        <div className="chart-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`chart-tab ${currentTabId === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTabId(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      {dataLoading && <Loading />}
      {!showData && !dataLoading && <p>No data available for the selected filters and segments.</p>}
      {showData && effectiveChartType === 'line' && <LineChart {...chartProps} />}
      {showData && effectiveChartType === 'lineV2' && <LineChartV2 {...rawChartProps} />}
      {showData && effectiveChartType === 'pie' && <PieChart {...chartProps} />}
      {showData && effectiveChartType === 'bar' && <BarChart {...chartProps} />}
      {showData && effectiveChartType === 'columnV2' && <ColumnChartV2 {...rawChartProps} />}
      {showData && effectiveChartType === 'barV2' && <ColumnChartV2 {...rawChartProps} />}
      {showData && effectiveChartType === 'pieV2' && <PieChartV2 {...rawChartProps} />}
      {showData && effectiveChartType === 'map' && <MapChart {...rawChartProps} mapId={effectiveChartOptions.mapId || 'uk-wpc'} />}
      {showData && effectiveChartType === 'mapWorld' && <MapChart {...rawChartProps} mapId='world' />}
      {showData && effectiveChartType === 'table' && <Table {...rawChartProps} />}
      {showData && effectiveChartType === 'timeSeriesTable' && <TimeSeriesTable {...rawChartProps} />}
      {showData && effectiveChartType === 'number' && <NumberChart {...rawChartProps} />}
      {showData && effectiveChartType === 'gauge' && <GaugeChart {...rawChartProps} />}
      {showData && effectiveChartType === 'pivotTable' && <PivotTable {...rawChartProps} />}
      {showData && effectiveChartType === 'confidenceIntervalLineChart' && <ConfidenceIntervalLineChart {...rawChartProps} />}
      {showData && effectiveChartType === 'json' && <div>{ JSON.stringify(data, null, 2) }</div>}
      {showData && chartComponent && React.createElement(chartComponent, { data, chartOptions, query })}
    </>
  )

  return (
    <div className='analytics-query'>
      {viewMode === 'sql' ? (
        sql ? (
          <pre className="analytics-query__sql-content">{sql}</pre>
        ) : (
          <p className="analytics-query__sql-unavailable">SQL not available (dev mode only)</p>
        )
      ) : (
        renderChart()
      )}
    </div>
  )
}

